import { useCallback } from 'react';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';

export const useOnboardingTasksSkip = ({ locationId }: { locationId: string }) => {
  const alerts = useAlert();
  const { t } = useTranslation('onboardingDashboard');

  const { data: isOnboardingTasksSkipped, isLoading } =
    PlatformLocationFeatureV1.Queries.useCheckLocationNotificationPreferenceQuery({
      request: {
        locationId,
        notificationEnum: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_ONBOARDING_TASKS_V2,
        sourceEnum: PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_ONBOARDING_TASK,
      },
      options: {
        enabled: !!locationId,
        retry: false,
        select: (response) =>
          response.currentStatus && response.currentStatus !== PlatformLocationFeatureV1.Types.Action.ACTION_UNKNOWN,
      },
    });

  const { mutate: skipOnboardingTasksMutate, isLoading: isSkipInProgress } =
    PlatformLocationFeatureV1.Mutations.useCreateOrUpdateLocationNotificationPreferenceMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Onboarding tasks skipped successfully.'));
        },
        onError: () => {
          alerts.error(t('Failed to skip tasks. Please try again later.'));
        },
      },
    });

  const skipOnboardingTasks = useCallback(() => {
    skipOnboardingTasksMutate({
      locationId,
      actionEnum: PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN,
      notificationEnum: PlatformLocationFeatureV1.Types.NotificationFeature.NOTIFICATION_FEATURE_ONBOARDING_TASKS_V2,
      sourceEnum: [PlatformLocationFeatureV1.Types.Source.SOURCE_DASHBOARD_ONBOARDING_TASK],
    });
  }, [locationId, skipOnboardingTasksMutate]);

  return {
    isOnboardingTasksSkipped,
    isLoading,
    isSkipInProgress,
    skipOnboardingTasks,
  };
};
